import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  EyeOff,
  Quote,
  Mic,
  MapPin,
  Tag,
  MessageSquareQuote,
  Timer,
  Globe,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Countdown, formatDeadline, useCountdown } from "@/components/Countdown";
import { SpeechBubbleArt, Logo, SignalLines } from "@/components/Logo";
import { useConfig, useMetrics } from "@/lib/useConfig";
import { useSeo, WEBSITE_JSONLD } from "@/lib/seo";
import { track } from "@/lib/analytics";

function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const nodes = ref.current?.querySelectorAll(".ss-reveal") || [];
    if (!("IntersectionObserver" in window)) {
      nodes.forEach((n) => n.classList.add("is-visible"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1 },
    );
    nodes.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, []);
  return ref;
}


const STEPS = [
  {
    no: "01",
    icon: MessageSquareQuote,
    title: "Tulis satu soalan",
    body: "Ringkas je. Yang jelas dan boleh dijawab, sampai 200 aksara.",
  },
  {
    no: "02",
    icon: ShieldCheck,
    title: "Butiran anda dijaga",
    body: "Nama dan e-mel hanya untuk kami hubungi anda kalau soalan terpilih. Tak dipapar awam, tak dijual.",
  },
  {
    no: "03",
    icon: Mic,
    title: "Kami bawa suara anda",
    body: "Soalan disemak ikut garis panduan terbuka. Yang terpilih kami bawa masuk sesi podcast.",
  },
];

const TRANSPARENCY = [
  {
    title: "Kritik boleh",
    body: "Kritik terhadap PMX, kerajaan, pembangkang atau dasar awam bukan sebab soalan ditolak. Kami nilai peraturan kandungan, bukan pendirian politik anda.",
  },
  {
    title: "Soalan sama kami kumpul",
    body: "Soalan yang hampir serupa kami letak bawah satu topik, supaya isu yang sama tak dikira berulang kali.",
  },
  {
    title: "Sebab tolak kami rekod",
    body: "Setiap penolakan ada kod sebab dan log audit. Anda boleh semak status soalan anda melalui pautan peribadi.",
  },
  {
    title: "Had kaedah kami akui",
    body: "Angka penyertaan ni gambaran peserta kempen ni je, bukan mewakili seluruh rakyat Malaysia.",
  },
  {
    title: "Kalau AI tolong, kami bagitahu",
    body: "Kalau pengesanan pendua atau risiko dibantu AI, kami nyatakan. Keputusan sensitif tetap disemak manusia.",
  },
];

export default function Landing() {
  const { data: config } = useConfig();
  const { data: metrics } = useMetrics();
  const revealRef = useReveal();
  const settings = config?.settings || {};
  const categories = config?.meta?.categories || [];
  const submissionsOpen = config?.submissions_open !== false;
  const phase = config?.phase;

  useSeo({
    title: "Satu Soalan untuk PMX",
    description:
      "Hantar satu soalan yang anda nak PMX jawab dalam sesi podcast 1 September. Isi guna Google, siap dalam seminit.",
    path: "/",
    jsonLd: WEBSITE_JSONLD,
  });

  const hasData = !!metrics?.has_data;

  const { ready: countdownReady, passed: countdownPassed, parts } = useCountdown(
    settings.deadline_at,
  );
  const urgencyLine = !countdownReady
    ? ""
    : countdownPassed
      ? "Penghantaran dah tutup."
      : parts.days > 7
        ? `Tinggal ${parts.days} hari lagi untuk hantar soalan anda.`
        : parts.days > 0
          ? `Tinggal ${parts.days} hari je. Jangan tunggu last minute.`
          : parts.hours > 0
            ? `Tinggal ${parts.hours} jam je hari ni. Hantar sekarang.`
            : "Tinggal beberapa minit je. Hantar sekarang.";
  const stateRows = (metrics?.state_breakdown || []).slice(0, 8);
  const categoryRows = (metrics?.category_breakdown || []).slice(0, 6);
  const topStateCount = stateRows[0]?.count || 0;

  const phaseNotice = {
    review: "Penghantaran dah tutup. Semua soalan yang masuk tengah disemak.",
    voting_open: "Penghantaran dah tutup. Pungutan suara peserta tengah berjalan.",
    closed: "Pemilihan dah selesai. Keputusan akan diterbitkan.",
    results_published: "Keputusan kempen dah diterbitkan.",
  }[phase];

  return (
    <div ref={revealRef} data-testid="landing-page">
      {/* ---------------- HERO ---------------- */}
      <section className="ss-hero-glow border-b border-border" data-testid="hero-section">
        <div className="ss-container grid items-center gap-12 py-14 sm:py-20 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
          <div>
            <h1 className="ss-h1" data-testid="hero-heading">
              <span className="ss-underline-red">Satu soalan je.</span> Apa yang anda betul-betul
              nak PMX jawab?
            </h1>
            <p className="ss-body mt-6 max-w-[60ch]" data-testid="hero-body">
              1 September ni Iskandar Rahman akan duduk sembang dengan PMX. Kali ni bukan soalan
              kami je yang dibawa, kami nak bawa soalan anda. Hantar satu soalan yang paling penting
              untuk anda.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              {submissionsOpen ? (
                <Button
                  asChild
                  size="lg"
                  className="w-full rounded-xl bg-[hsl(var(--ss-accent))] px-6 font-semibold text-white hover:bg-[hsl(var(--ss-accent))]/85 sm:w-auto"
                  data-testid="hero-primary-cta"
                >
                  <Link to="/hantar-soalan" onClick={() => track("hero_cta_clicked")}>
                    Hantar Soalan Saya
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              ) : (
                <div
                  className="rounded-xl border border-border bg-card px-5 py-3 text-sm font-semibold text-foreground"
                  data-testid="hero-closed-notice"
                >
                  Penghantaran dah tutup. Terima kasih kepada semua yang hantar soalan.
                </div>
              )}
            </div>

            <p
              className="mt-3 text-[13px] text-[hsl(var(--muted-foreground))]"
              data-testid="hero-cta-helper"
            >
              Lapan langkah ringkas. Kami minta nama dan e-mel supaya boleh hubungi anda kalau
              soalan anda terpilih.
            </p>

            <p
              className="mt-2 min-h-[20px] text-[13px] font-medium text-foreground"
              data-testid="hero-host-line"
            >
              {settings.host_name ? `Sesi ini dihoskan oleh ${settings.host_name}.` : ""}
            </p>

            {phaseNotice && (
              <p
                className="mt-4 rounded-xl border border-border bg-card px-4 py-2.5 text-[13px] font-semibold text-foreground"
                data-testid="phase-notice"
              >
                {phaseNotice}
              </p>
            )}

            <p
              className="mt-6 flex min-h-[92px] max-w-[70ch] items-start gap-3.5 rounded-2xl border border-[hsl(var(--ss-line))] bg-[hsl(var(--ss-tint))] px-4 py-3.5 text-[13px] leading-relaxed text-[hsl(var(--foreground))]"
              data-testid="hero-disclaimer"
            >
              {settings.organizer_logo_url && (
                <img
                  src={settings.organizer_logo_url}
                  alt={`Logo ${settings.organizer_name || "penganjur"}`}
                  className="h-12 w-12 shrink-0 rounded-lg bg-[hsl(var(--ss-panel))] object-cover"
                  width="48"
                  height="48"
                  loading="lazy"
                  data-testid="hero-organizer-logo"
                />
              )}
              <span>
              {settings.disclaimer_text ||
                "Inisiatif bebas oleh SembangOhhyeahh. Bukan laman rasmi Pejabat Perdana Menteri atau Kerajaan Malaysia. Penggunaan nama PMX merujuk kepada tetamu sesi podcast."}
              </span>
            </p>
          </div>

          {/* Hero visual */}
          <div className="relative">
            <div className="ss-card relative overflow-hidden p-6 sm:p-7" data-testid="hero-visual">
              <SpeechBubbleArt className="pointer-events-none absolute -right-10 -top-10 h-36 w-56 opacity-40" />
              <div className="relative">
                <div className="flex items-center gap-2.5">
                  <Logo size={30} withSignals={false} />
                  <span className="text-xs font-bold uppercase tracking-[0.16em] text-[hsl(var(--muted-foreground))]">
                    Contoh soalan peserta
                  </span>
                </div>
                <blockquote className="mt-5 border-l-2 border-[hsl(var(--ss-accent2))] pl-4">
                  <Quote className="h-4 w-4 text-[hsl(var(--ss-accent2))]" aria-hidden="true" />
                  <p className="mt-2 text-[17px] font-bold leading-snug text-foreground sm:text-lg">
                    “Apa langkah paling segera untuk turunkan kos sara hidup keluarga muda?”
                  </p>
                  <footer className="mt-3 flex flex-wrap items-center gap-2 text-[11px] font-bold uppercase tracking-[0.1em] text-[hsl(var(--muted-foreground))]">
                    <span className="rounded-full bg-[hsl(var(--secondary))] px-2.5 py-1 text-foreground">
                      Kos Sara Hidup
                    </span>
                    <span>Maksimum 200 aksara</span>
                  </footer>
                </blockquote>

                <div
                  className="mt-7 rounded-2xl bg-[hsl(var(--ss-panel))] p-5 text-white"
                  data-testid="hero-countdown-panel"
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-[hsl(var(--ss-accent2))]">
                      <span
                        className="ss-pulse-dot inline-block h-2 w-2 rounded-full bg-[hsl(var(--ss-accent2))]"
                        aria-hidden="true"
                      />
                      Masa berbaki untuk hantar
                    </p>
                    <SignalLines />
                  </div>
                  <div className="mt-4 min-h-[104px] [&_div>div]:border-white/15 [&_div>div]:bg-card/[0.06] [&_div>div]:text-white">
                    {settings.deadline_at ? (
                      <Countdown deadlineIso={settings.deadline_at} variant="hero" />
                    ) : (
                      <p className="text-sm text-white/70">Tarikh akhir akan dikemas kini.</p>
                    )}
                  </div>
                  <p
                    className="mt-4 min-h-[20px] text-[13px] font-bold leading-snug text-white"
                    data-testid="hero-countdown-urgency"
                  >
                    {urgencyLine}
                  </p>
                  <p className="mt-1 min-h-[16px] text-[11px] leading-relaxed text-white/65">
                    {settings.deadline_at
                      ? `Ditutup ${formatDeadline(settings.deadline_at)} (${
                          settings.timezone || "Asia/Kuala_Lumpur"
                        }).`
                      : ""}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- METRICS ---------------- */}
      <section className="ss-section bg-card" data-testid="metrics-section">
        <div className="ss-container">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h2 className="ss-h2">Penyertaan setakat ni</h2>
              <p className="ss-body mt-2">Ikut peserta kempen ni je. Dipapar sebagai angka agregat.</p>
            </div>
            <span className="ss-chip cursor-default">
              <Sparkles className="h-4 w-4 text-[hsl(var(--ss-accent))]" aria-hidden="true" /> Dikira dari soalan yang masuk
            </span>
          </div>

          {!hasData ? (
            <div
              className="ss-card mt-8 p-8 text-center"
              data-testid="metrics-empty-state"
              aria-live="polite"
            >
              <p className="text-base font-bold text-foreground">
                Jadi orang pertama yang hantar soalan.
              </p>
              <p className="ss-body mx-auto mt-2 max-w-md">
                Angka penyertaan akan naik sebaik soalan pertama masuk.
              </p>
            </div>
          ) : (
            <div
              className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
              data-testid="metrics-grid"
            >
              {[
                {
                  icon: MessageSquareQuote,
                  label: "Soalan masuk",
                  value: metrics.verified_questions,
                  testId: "metric-verified-questions",
                },
                {
                  icon: MapPin,
                  label: "Negeri yang join",
                  value: metrics.states_participating,
                  testId: "metric-states",
                },
                {
                  icon: Tag,
                  label: "Kategori isu",
                  value: metrics.categories_active,
                  testId: "metric-categories",
                },
              ].map((m) => (
                <div
                  key={m.label}
                  className="ss-card ss-card-hover ss-reveal p-5"
                  data-testid={m.testId}
                >
                  <m.icon className="h-5 w-5 text-[hsl(var(--ss-accent))]" aria-hidden="true" />
                  <p className="mt-4 text-3xl font-extrabold tabular-nums tracking-tight text-foreground">
                    {m.value ?? 0}
                  </p>
                  <p className="mt-1 text-[13px] font-medium text-[hsl(var(--muted-foreground))]">
                    {m.label}
                  </p>
                </div>
              ))}
              <div className="ss-card ss-card-hover ss-reveal p-5" data-testid="metric-time-left">
                <Timer className="h-5 w-5 text-[hsl(var(--ss-accent))]" aria-hidden="true" />
                <p className="mt-4 text-xl font-extrabold tracking-tight text-foreground">
                  {settings.deadline_at ? (
                    <Countdown deadlineIso={settings.deadline_at} variant="inline" />
                  ) : (
                    ", "
                  )}
                </p>
                <p className="mt-1 text-[13px] font-medium text-[hsl(var(--muted-foreground))]">
                  Masa berbaki
                </p>
              </div>
            </div>
          )}

          {hasData && stateRows.length > 0 && (
            <div className="ss-card ss-reveal mt-5 p-6" data-testid="live-state-breakdown">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <h3 className="ss-h3">Taburan negeri</h3>
                  <p className="ss-body mt-1 text-[14px]">
                    Negeri yang paling banyak hantar soalan setakat ni.
                  </p>
                </div>
                <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-[hsl(var(--muted-foreground))]">
                  Kemas kini setiap 30 saat
                </span>
              </div>

              <ul className="mt-6 space-y-3">
                {stateRows.map((row) => {
                  const pct = topStateCount ? Math.round((row.count / topStateCount) * 100) : 0;
                  const share = metrics.verified_questions
                    ? Math.round((row.count / metrics.verified_questions) * 100)
                    : 0;
                  return (
                    <li key={row.state} data-testid={`state-row-${row.state}`}>
                      <div className="flex items-center justify-between gap-3 text-[13px]">
                        <span className="font-semibold text-foreground">{row.state}</span>
                        <span className="tabular-nums text-[hsl(var(--muted-foreground))]">
                          {row.count} soalan · {share}%
                        </span>
                      </div>
                      <div
                        className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-[hsl(var(--secondary))]"
                        role="progressbar"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={share}
                        aria-label={`Taburan soalan dari ${row.state}`}
                      >
                        <div
                          className="h-full rounded-full bg-[hsl(var(--ss-accent))] transition-[width] duration-500 motion-reduce:transition-none"
                          style={{ width: `${Math.max(6, pct)}%` }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>

              {categoryRows.length > 0 && (
                <div className="mt-7 border-t border-[hsl(var(--ss-line))] pt-5">
                  <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-[hsl(var(--muted-foreground))]">
                    Isu paling banyak dibangkitkan
                  </h3>
                  <ul className="mt-3 flex flex-wrap gap-2" data-testid="live-category-breakdown">
                    {categoryRows.map((row) => (
                      <li
                        key={row.category}
                        className="rounded-full border border-[hsl(var(--ss-line))] bg-[hsl(var(--ss-tint))] px-3 py-1.5 text-[12.5px] font-semibold text-foreground"
                        data-testid={`category-row-${row.category}`}
                      >
                        {row.category}{" "}
                        <span className="tabular-nums text-[hsl(var(--muted-foreground))]">
                          {row.count}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ---------------- HOW IT WORKS ---------------- */}
      <section
        id="cara-ia-berfungsi"
        className="ss-section border-y border-[hsl(var(--ss-line))] bg-[hsl(var(--ss-tint))]"
        data-testid="how-it-works"
      >
        <div className="ss-container">
          <h2 className="ss-h2 max-w-2xl">Macam mana ia jalan</h2>
          <p className="ss-body mt-3 max-w-[60ch]">
            Tiga langkah je, siap dalam seminit.
          </p>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {STEPS.map((s) => (
              <div
                key={s.no}
                className="ss-card ss-card-hover ss-reveal p-6"
                data-testid={`how-step-${s.no}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[13px] font-medium tracking-[0.18em] text-[hsl(var(--muted-foreground))]">
                    {s.no}
                  </span>
                  <s.icon className="h-5 w-5 text-[hsl(var(--ss-accent))]" aria-hidden="true" />
                </div>
                <h3 className="ss-h3 mt-5">{s.title}</h3>
                <p className="ss-body mt-2 text-[14px]">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- CATEGORIES ---------------- */}
      <section className="ss-section bg-card" data-testid="categories-section">
        <div className="ss-container">
          <h2 className="ss-h2 max-w-2xl">Isu apa yang anda nak bangkitkan?</h2>
          <p className="ss-body mt-3 max-w-[60ch]">
            Tekan satu kategori, borang terus terbuka dengan kategori tu.
          </p>
          <div className="mt-8 flex flex-wrap gap-2.5" data-testid="category-chips">
            {categories.length === 0
              ? Array.from({ length: 6 }).map((_, i) => (
                  <span
                    key={i}
                    className="h-10 w-32 animate-pulse rounded-full bg-[hsl(var(--secondary))]"
                  />
                ))
              : categories.map((cat) => (
                  <Link
                    key={cat}
                    to={`/hantar-soalan?kategori=${encodeURIComponent(cat)}`}
                    className="ss-chip"
                    data-testid={`category-chip-${cat}`}
                  >
                    {cat}
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </Link>
                ))}
          </div>
        </div>
      </section>

      {/* ---------------- TRANSPARENCY ---------------- */}
      <section
        className="ss-section border-y border-border bg-[hsl(var(--ss-panel))] text-white"
        data-testid="transparency-section"
      >
        <div className="ss-container grid gap-10 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-[hsl(var(--ss-accent2))]">
              Ketelusan
            </p>
            <h2 className="ss-h2 mt-4 text-white">
              Bukan sekadar kumpul soalan. Prosesnya kena boleh dipercayai.
            </h2>
            <p className="mt-5 max-w-[52ch] text-[15px] leading-relaxed text-white/75">
              Peraturan moderasi, kod sebab tolak dan had kempen ni kami letak awal-awal. Anda nilai sendiri.
            </p>
            <Button
              asChild
              className="mt-7 rounded-xl bg-card font-semibold text-foreground hover:bg-[hsl(var(--secondary))]"
              data-testid="transparency-cta"
            >
              <Link to="/cara-pemilihan" onClick={() => track("methodology_viewed")}>
                Baca cara pemilihan penuh
              </Link>
            </Button>
          </div>
          <ul className="grid gap-4 sm:grid-cols-2">
            {TRANSPARENCY.map((item) => (
              <li
                key={item.title}
                className="ss-reveal rounded-3xl border border-white/12 bg-card/[0.05] p-5"
              >
                <div className="flex items-start gap-2">
                  <ShieldCheck
                    className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--ss-accent2))]"
                    aria-hidden="true"
                  />
                  <h3 className="text-[15px] font-bold leading-snug text-white">{item.title}</h3>
                </div>
                <p className="mt-2.5 text-[13.5px] leading-relaxed text-white/75">{item.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ---------------- ORGANIZER ---------------- */}
      <section className="ss-section border-t border-[hsl(var(--ss-line))] bg-[hsl(var(--ss-tint))]" data-testid="organizer-section">
        <div className="ss-container">
          <h2 className="ss-h2">Siapa buat kempen ni?</h2>
          <div className="ss-card mt-8 grid gap-8 p-6 sm:p-8 lg:grid-cols-[auto_1fr]">
            <div className="flex items-center gap-4">
              {settings.organizer_logo_url ? (
                <img
                  src={settings.organizer_logo_url}
                  alt={`Logo ${settings.organizer_name || "penganjur"}`}
                  className="h-20 w-20 rounded-2xl border border-[hsl(var(--ss-line))] bg-[hsl(var(--ss-panel))] object-cover"
                  width="80"
                  height="80"
                  loading="lazy"
                  data-testid="organizer-logo"
                />
              ) : (
                <div className="flex h-20 w-20 items-center justify-center rounded-2xl border border-dashed border-border bg-[hsl(var(--secondary))]">
                  <Logo size={32} withSignals={false} />
                </div>
              )}
            </div>
            <div>
              <h3 className="ss-h3" data-testid="organizer-name">
                {settings.organizer_name || "SembangOhhyeahh"}
              </h3>
              <p className="ss-body mt-3 max-w-[68ch]" data-testid="organizer-description">
                {settings.organizer_description ||
                  "Maklumat penganjur akan dikemas kini melalui panel admin."}
              </p>
              {settings.host_name && (
                <div
                  className="mt-6 flex items-center gap-4 rounded-2xl border border-[hsl(var(--ss-line))] bg-[hsl(var(--ss-tint))] p-4"
                  data-testid="host-card"
                >
                  {settings.host_image_url && (
                    <img
                      src={settings.host_image_url}
                      alt={`Hos ${settings.host_name}`}
                      className="h-16 w-16 shrink-0 rounded-xl border border-[hsl(var(--ss-line))] object-cover object-[center_25%]"
                      width="64"
                      height="64"
                      loading="lazy"
                      data-testid="organizer-host-image"
                    />
                  )}
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--muted-foreground))]">
                      Dihoskan oleh
                    </p>
                    <p className="mt-1 text-[15px] font-bold text-foreground" data-testid="host-name">
                      {settings.host_name}
                    </p>
                    <p className="text-[13px] text-[hsl(var(--muted-foreground))]" data-testid="host-role">
                      {settings.host_role || "Hos podcast"}
                    </p>
                  </div>
                </div>
              )}

              <div className="mt-5 flex flex-wrap gap-2">
                {settings.organizer_website && (
                  <a
                    href={settings.organizer_website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ss-chip"
                    data-testid="organizer-website"
                  >
                    <Globe className="h-4 w-4" aria-hidden="true" />
                    Laman web
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- FINAL CTA ---------------- */}
      <section className="ss-section border-t border-border" data-testid="final-cta-section">
        <div className="ss-container max-w-3xl text-center">
          <h2 className="ss-h2">
            Satu soalan pun boleh buka cerita besar. Apa yang paling anda nak PMX jawab?
          </h2>
          {submissionsOpen ? (
            <Button
              asChild
              size="lg"
              className="mt-8 rounded-xl bg-[hsl(var(--ss-accent))] px-7 font-semibold text-white hover:bg-[hsl(var(--ss-accent))]/85"
              data-testid="final-cta-button"
            >
              <Link to="/hantar-soalan" onClick={() => track("hero_cta_clicked")}>
                Hantar Soalan Saya
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          ) : (
            <p
              className="mt-8 text-sm font-semibold text-foreground"
              data-testid="final-cta-closed"
            >
              Penghantaran dah tutup. Terima kasih kepada semua yang hantar soalan.
            </p>
          )}
          <p className="mx-auto mt-6 max-w-[62ch] text-[13px] leading-relaxed text-[hsl(var(--muted-foreground))]">
            Soalan terpilih kami bawa untuk pertimbangan dalam sesi podcast. Penyertaan sukarela dan
            butiran anda hanya digunakan untuk hubungan berkaitan kempen ini.
          </p>
        </div>
      </section>
    </div>
  );
}
